import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  Loader2,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";

const MAX_ATTEMPTS = 12; // ~24 seconds total
const RETRY_MS = 2000;

export default function BetalingSucces() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id") || params.get("transactionid");
  const isMock = params.get("mock") === "1";

  const [phase, setPhase] = useState("verifying"); // verifying | confirmed | error
  const [attempt, setAttempt] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!orderId) {
      setPhase("error");
      setErrorMsg("Geen order-referentie gevonden in de URL.");
      return;
    }

    let cancelled = false;
    let timer;

    const tick = async (count) => {
      if (cancelled) return;
      try {
        // First attempt: call /verify (mock or real). Backend either marks
        // mock-payment as complete OR queries MultiSafepay actively.
        if (count === 0 && isMock) {
          await api.post(`/payments/${orderId}/complete-mock`).catch(() => null);
        }

        const { data } = await api.post(`/payments/${orderId}/verify`);
        if (cancelled) return;

        if (data.payment_status === "completed") {
          setPhase("confirmed");
          setTimeout(() => navigate(`/rapport/${orderId}`, { replace: true }), 900);
          return;
        }
      } catch (e) {
        // 404 = analyse niet gevonden; 502 = MSP onbereikbaar — show error
        const status = e?.response?.status;
        if (status === 404) {
          if (!cancelled) {
            setPhase("error");
            setErrorMsg("De betaling kon niet worden teruggevonden. Neem contact op met info@offerteexpert.be.");
          }
          return;
        }
        // 502 / network error → keep retrying
      }

      const next = count + 1;
      setAttempt(next);
      if (next >= MAX_ATTEMPTS) {
        if (!cancelled) {
          setPhase("error");
          setErrorMsg(
            "Je betaling werd doorgegeven, maar we konden ze nog niet bevestigen. " +
              "Geen zorgen — bewaar je referentie en ververs over een minuut. Heb je hulp nodig? info@offerteexpert.be"
          );
        }
        return;
      }
      timer = setTimeout(() => tick(next), RETRY_MS);
    };

    tick(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [orderId, isMock]);

  const pct = Math.min(95, Math.round((attempt / MAX_ATTEMPTS) * 100));

  return (
    <div className="min-h-screen bg-oc-bg flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-oc-border rounded-oc p-10 text-center shadow-oc-card">
        {phase === "verifying" && (
          <>
            <div className="w-14 h-14 rounded-full bg-oc-subtle mx-auto flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-oc-secondary animate-spin" strokeWidth={2.2} />
            </div>
            <h1 className="mt-5 font-display text-oc-primary text-[26px] font-bold">
              Betaling wordt bevestigd…
            </h1>
            <p className="mt-2 text-oc-textSecondary text-[15px] leading-relaxed">
              We controleren de betaling rechtstreeks bij MultiSafepay. Dit duurt doorgaans 2 à 10 seconden.
            </p>
            <div className="mt-6">
              <Progress value={pct} className="h-2 bg-oc-subtle" />
              <p className="mt-2 text-[12px] font-mono text-oc-textSecondary">{pct}%</p>
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 text-[12px] text-oc-textSecondary">
              <ShieldCheck className="w-3.5 h-3.5" /> Beveiligd door MultiSafepay
            </div>
            {orderId && (
              <p className="mt-4 text-[12px] text-oc-textSecondary font-mono">Referentie: {orderId}</p>
            )}
          </>
        )}

        {phase === "confirmed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-oc-fair/10 mx-auto flex items-center justify-center animate-[pulse-soft_2s_ease-out_infinite]">
              <CheckCircle2 className="w-7 h-7 text-oc-fair" strokeWidth={2.2} />
            </div>
            <h1 className="mt-5 font-display text-oc-primary text-[26px] font-bold">Betaling ontvangen</h1>
            <p className="mt-2 text-oc-textSecondary text-[15px] leading-relaxed">
              Bedankt voor je vertrouwen. Je rapport wordt nu samengesteld. We sturen je automatisch door…
            </p>
            <Link to={`/rapport/${orderId}`} className="block mt-7">
              <Button
                data-testid="go-to-report"
                className="w-full h-12 bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn font-semibold"
              >
                Bekijk mijn rapport <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </>
        )}

        {phase === "error" && (
          <>
            <div className="w-14 h-14 rounded-full bg-oc-high/10 mx-auto flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-oc-high" strokeWidth={2.2} />
            </div>
            <h1 className="mt-5 font-display text-oc-primary text-[24px] font-bold">
              Betaling nog niet bevestigd
            </h1>
            <p className="mt-2 text-oc-textSecondary text-[14px] leading-relaxed">{errorMsg}</p>
            {orderId && (
              <p className="mt-3 text-[12px] text-oc-textSecondary font-mono">Referentie: {orderId}</p>
            )}
            <div className="mt-6 grid sm:grid-cols-2 gap-2">
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="rounded-oc-btn border-oc-border text-oc-primary hover:bg-oc-subtle font-semibold"
              >
                Opnieuw controleren
              </Button>
              <Link to={`/rapport/${orderId}`} className="contents">
                <Button className="w-full bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn font-semibold">
                  Probeer mijn rapport
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-[12px] text-oc-textSecondary">
              Hulp nodig? <a href="mailto:info@offerteexpert.be" className="text-oc-secondary underline">info@offerteexpert.be</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
